import React, { useMemo, useState } from 'react';
import { convertSpeed, speedUnit, convertFeet, feetUnit } from '../utils/units';

function formatPeak(value, unit) {
  if (value == null || !Number.isFinite(value)) return '--';
  if (unit === 'G') return `${value.toFixed(2)} G`;
  return `${Math.round(value)}${unit ? ` ${unit}` : ''}`;
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  summary: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  chip: {
    padding: '8px 10px',
    borderRadius: 999,
    border: '1px solid rgba(138,180,200,0.24)',
    background: 'rgba(13,27,42,0.72)',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 1020,
  },
  th: {
    padding: '10px 12px',
    background: 'var(--color-surface-light)',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    fontFamily: 'var(--font-data)',
    fontSize: 13,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  firstCol: {
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    background: 'var(--color-surface)',
    zIndex: 1,
    minWidth: 84,
    fontFamily: 'var(--font-heading)',
    fontWeight: 700,
  },
  note: {
    textAlign: 'left',
    whiteSpace: 'normal',
    minWidth: 220,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
  },
  scorePill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 38,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
  },
  empty: {
    padding: '18px 20px',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    border: '1px solid var(--color-border)',
    borderRadius: 10,
  },
  positiveDelta: {
    color: '#27ae60',
  },
  negativeDelta: {
    color: '#e74c3c',
  },
};

function getScorePillStyle(score) {
  if (score >= 90) return { color: '#1c7c54', background: 'rgba(39, 174, 96, 0.18)' };
  if (score >= 75) return { color: '#d4a017', background: 'rgba(243, 156, 18, 0.18)' };
  return { color: '#c44536', background: 'rgba(231, 76, 60, 0.18)' };
}

export default function BrakingSummaryTable({
  rows,
  sourceLabel,
  peakLabel,
  peakUnit,
  avgStartSpread,
  referenceLabel = 'Best 3 laps',
  user,
}) {
  const fmtFt = (v) => {
    if (v == null || !Number.isFinite(v)) return '--';
    return `${Math.round(convertFeet(v, user))} ${feetUnit(user)}`;
  };

  const [sortKey, setSortKey] = useState('brakeStartSpreadFt');
  const [sortDir, setSortDir] = useState('desc');

  const sortedRows = useMemo(() => {
    return [...(rows || [])].sort((a, b) => {
      const aValue = sortKey.includes('.') ? sortKey.split('.').reduce((acc, key) => acc?.[key], a) : a?.[sortKey];
      const bValue = sortKey.includes('.') ? sortKey.split('.').reduce((acc, key) => acc?.[key], b) : b?.[sortKey];
      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      const base = aValue > bValue ? 1 : -1;
      return sortDir === 'asc' ? base : -base;
    });
  }, [rows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'label' ? 'asc' : 'desc');
  };

  if (!rows?.length) {
    return <div style={styles.empty}>No braking summary available for the selected laps.</div>;
  }

  const columns = [
    { key: 'label', label: 'Corner', align: 'left' },
    { key: 'brakeStartSpreadFt', label: 'Brake Start Spread' },
    { key: 'trailBrakeAvgFt', label: 'Release' },
    { key: 'trailBrakeSpreadFt', label: 'Rel Spread' },
    { key: 'peakBrakeAvg', label: peakLabel || 'Peak Brake' },
    { key: 'minSpeedAvgMph', label: 'Min Speed' },
  ];

  return (
    <div style={styles.wrapper}>
      <div style={styles.summary}>
        <div style={styles.chip}>Source: {sourceLabel || '--'}</div>
        <div style={styles.chip}>Reference: {referenceLabel}</div>
        <div style={styles.chip}>Average start spread: {fmtFt(avgStartSpread)}</div>
        <div style={styles.chip}>Peak metric: {peakLabel || 'Peak Brake'}{peakUnit ? ` (${peakUnit})` : ''}</div>
      </div>
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ ...styles.th, ...(column.align === 'left' ? { textAlign: 'left' } : {}) }}
                  onClick={() => handleSort(column.key)}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.cornerId}>
                <td style={{ ...styles.td, ...styles.firstCol }}>{row.label}</td>
                <td style={styles.td}>{fmtFt(row.brakeStartSpreadFt)}</td>
                <td style={styles.td}>{fmtFt(row.trailBrakeAvgFt)}</td>
                <td style={styles.td}>{fmtFt(row.trailBrakeSpreadFt)}</td>
                <td style={styles.td}>{formatPeak(row.peakBrakeAvg, peakUnit)}</td>
                <td style={styles.td}>{row.minSpeedAvgMph != null ? `${convertSpeed(row.minSpeedAvgMph, user).toFixed(1)} ${speedUnit(user).toLowerCase()}` : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
